import React, { useState } from 'react';
import { Button, Text } from '@chakra-ui/react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as PCIActions from '../../../../store/student/preCasInterview/actions';
import { createNavigateForwardAction, createNavigateToAction } from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { toggleTaskMenu } from '../../../../store/ui/actions';

import {
  TextareaWithConfirm,
  CheckboxOptionsWithConfirm,
  AutocompleteInputWithConfirm,
  InputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  SubHeading
} from 'shared-components';
import { universities } from 'data';

import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';

import { ICheckbox } from '../../../../types';
import { titleAppend } from '../../../../utilities/head';

const isUniNameValid = (name: string) => {
  return universities.filter((uni: string) => uni.toLowerCase() === name.toLowerCase()).length > 0;
};

const InterviewQPage2 = () => {
  const dispatch = useDispatch();
  const pageTitle = 'Pre-CAS Questionnaire';
  const currentStep = 2;
  const steps = 4;

  const onNext = () => {
    dispatch(StudentActions.savePreCasInterview(Pages.PAGE2));
    dispatch(createNavigateForwardAction());
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(PCIActions.createUpdatePCIFieldAction(fieldName, value, refValue));

  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const pageConfig = useSelector((state: ApplicationState) => state.config.Interview.Page2);
  const {
    campus = '',
    considerOtherUni = true,
    otherUni1 = '',
    otherUni2 = '',
    selectedResearchMethods = [],
    selectedResearchMethodsOther = '',
    whyNotOtherUni = '',
    considerOtherCourses = undefined,
    considerOtherCourse = '',
    whyNotConsiderOtherCourse = '',
    courseAlignFutureCareer = undefined,
    courseAlignFutureCareerOther = ''
  } = useSelector((state: ApplicationState) => state.student.preCasInterview);

  const [otherUni1Error, setOtherUni1Error] = useState('');
  const [otherUni2Error, setOtherUni2Error] = useState('');
  const [university1Suggestions, setUniversity1Suggestions] = useState([]);
  const [university2Suggestions, setUniversity2Suggestions] = useState([]);

  const onUniversityChange = (evt: any, suggestionStateName: string, stateName: string) => {
    const value = evt?.currentTarget?.value.trim() || '';
    const suggestions = universities.filter((uni: string) => uni.toLowerCase().includes(value.toLowerCase()));

    if (suggestionStateName === 'university1Suggestions') {
      setUniversity1Suggestions(suggestions.slice(0));
    } else if (suggestionStateName === 'university2Suggestions') {
      setUniversity2Suggestions(suggestions.slice(0));
    }

    const otherUniError = isUniNameValid(value) ? '' : 'Please select a university';
    if (stateName === 'otherUni1') {
      setOtherUni1Error(otherUniError);
    } else if (stateName === 'otherUni2') {
      setOtherUni2Error(otherUniError);
    }
    onChange(stateName, value);
  };

  const onBlurOtherUni = (evt: React.FormEvent<HTMLInputElement>, stateName: string) => {
    const value = evt?.currentTarget?.value.trim() || '';
    const otherUniError = isUniNameValid(value) ? '' : 'Please select a university';
    if (stateName === 'otherUni1') {
      setOtherUni1Error(otherUniError);
    } else if (stateName === 'otherUni2') {
      setOtherUni2Error(otherUniError);
    }
    onChange(stateName, value);
  };

  const onFocusOtherUni = (event: React.FormEvent<HTMLInputElement>, stateName: string) => {
    if (stateName === 'otherUni1Error') {
      setOtherUni1Error('');
    } else if (stateName === 'otherUni2Error') {
      setOtherUni2Error('');
    }
  };

  const getResearchMethods = (): ICheckbox[] => {
    const researchMethods = [
      'University website',
      'University prospectus',
      'Someone I trust recommended it',
      'An agent or representative told me about the university',
      'Other'
    ];

    return researchMethods.map((method: string) => ({
      id: method,
      label: method,
      isTicked: !!(selectedResearchMethods && selectedResearchMethods.includes(method)),
      readOnly: false
    }));
  };

  const onChangeResearchMethod = (selectedMethod: string, isTicked: boolean) => {
    let selectedMethods = [];
    const currentMethods = selectedResearchMethods.slice(0);

    if (!isTicked) {
      // remove from list
      selectedMethods = currentMethods.filter((reason: string) => {
        return reason !== selectedMethod;
      });
    } else {
      // add to list
      currentMethods.push(selectedMethod);
      selectedMethods = currentMethods.slice(0);
    }
    onChange(Q.SELECTED_RESEARCH_METHODS, selectedMethods);
  };

  const isConsiderOtherUniComplete =
    considerOtherUni !== undefined &&
    (considerOtherUni === true
      ? otherUni1 !== '' &&
        otherUni2 !== '' &&
        selectedResearchMethods.length > 0 &&
        (selectedResearchMethods.includes('Other') ? selectedResearchMethodsOther !== '' : true)
      : whyNotOtherUni !== '');

  const isConsiderOtherCoursesComplete =
    considerOtherCourses !== undefined &&
    (considerOtherCourses === true ? considerOtherCourse !== '' : whyNotConsiderOtherCourse !== '');

  const isCourseAlignFutureCareerComplete =
    courseAlignFutureCareer !== undefined &&
    (courseAlignFutureCareer === false ? courseAlignFutureCareerOther !== '' : true);

  const complete =
    campus !== '' && isConsiderOtherUniComplete && isConsiderOtherCoursesComplete && isCourseAlignFutureCareerComplete;

  const isFormValid = considerOtherUni ? otherUni1Error === '' && otherUni2Error === '' : true;

  return (
    <QuestionPage
      complete={complete && isFormValid}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={pageTitle}
    >
      <Helmet>
        <title>
          {`${pageTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <InputWithConfirm
        key={Q.CAMPUS}
        id={Q.CAMPUS}
        value={campus}
        label={'Which campus are you studying at?'}
        inputHelperText={pageConfig.Campus.helpText}
        onChange={(evt: any) => onChange(Q.CAMPUS, evt.currentTarget.value)}
      />

      <SubHeading>Please list two other universities you researched as part of your UK study plans</SubHeading>

      <Text fontSize="sm">
        "Research" includes looking at university websites, checking out their brochures, talking to staff at education
        fairs, or getting information from an agent.
      </Text>

      {considerOtherUni === true && (
        <>
          <Button id="courseNotFound" onClick={() => onChange(Q.CONSIDER_OTHER_UNI, false)} mb={6}>
            I did not research other universities
          </Button>

          <AutocompleteInputWithConfirm
            id="select-university-1"
            key="select-university-1"
            label="Select University 1"
            onChange={(evt: any) => onUniversityChange(evt, 'university1Suggestions', Q.OTHER_UNI_1)}
            value={otherUni1}
            inputHelperText={pageConfig.ConsiderOtherUni.helpText}
            showErrorText={!!otherUni1Error}
            errorText={otherUni1Error}
            suggestions={university1Suggestions}
            onBlur={(evt: any) => onBlurOtherUni(evt, Q.OTHER_UNI_1)}
            onFocus={(evt: any) => onFocusOtherUni(evt, 'otherUni1Error')}
          />

          <AutocompleteInputWithConfirm
            id="select-university-2"
            key="select-university-2"
            label="Select University 2"
            onChange={(evt: any) => onUniversityChange(evt, 'university2Suggestions', Q.OTHER_UNI_2)}
            value={otherUni2}
            inputHelperText={pageConfig.ConsiderOtherUni.helpText}
            showErrorText={!!otherUni2Error}
            errorText={otherUni2Error}
            suggestions={university2Suggestions}
            onBlur={(evt: any) => onBlurOtherUni(evt, Q.OTHER_UNI_2)}
            onFocus={(evt: any) => onFocusOtherUni(evt, 'otherUni2Error')}
          />

          <CheckboxOptionsWithConfirm
            id="research-methods"
            key={Q.SELECTED_RESEARCH_METHODS}
            inputHelperText={pageConfig.UniResearchMethod.helpText}
            checkboxes={getResearchMethods()}
            labelText="How did you research these universities?"
            onChange={onChangeResearchMethod}
          />

          {selectedResearchMethods && selectedResearchMethods.findIndex(method => method === 'Other') > -1 && (
            <InputWithConfirm
              key={Q.SELECTED_RESEARCH_METHODS_OTHER}
              id={Q.SELECTED_RESEARCH_METHODS_OTHER}
              value={selectedResearchMethodsOther}
              label={'Please provide more information'}
              onChange={(evt: any) => onChange(Q.SELECTED_RESEARCH_METHODS_OTHER, evt.currentTarget.value)}
            />
          )}
        </>
      )}

      {considerOtherUni === false && (
        <>
          <Button id="courseNotFound" onClick={() => onChange(Q.CONSIDER_OTHER_UNI, true)} mb={6}>
            I did research other universities
          </Button>

          <TextareaWithConfirm
            key={Q.WHY_NOT_OTHER_UNI}
            id={Q.WHY_NOT_OTHER_UNI}
            value={whyNotOtherUni}
            label={'Please explain why you did no research on other universities while planning your overseas study'}
            inputHelperText={pageConfig.ConsiderOtherUniReason.helpText}
            onChange={(evt: any) => onChange(Q.WHY_NOT_OTHER_UNI, evt.currentTarget.value)}
          />
        </>
      )}

      <RadioOptionsWithConfirm
        label="Did you consider any other course?"
        id={Q.CONSIDER_OTHER_COURSES}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: considerOtherCourses === true
          },
          {
            char: 'N',
            label: 'No',
            selected: considerOtherCourses === false
          }
        ]}
        maxQuestions={2}
        isInline={true}
        onClick={(question: IQuestion) => {
          onChange(Q.CONSIDER_OTHER_COURSES, question.label === 'Yes');
        }}
      />

      {considerOtherCourses === true && (
        <InputWithConfirm
          key={Q.CONSIDER_OTHER_COURSE}
          id={Q.CONSIDER_OTHER_COURSE}
          value={considerOtherCourse}
          label={'What course did you consider?'}
          inputHelperText={pageConfig.ConisderOtherCourseName?.helpText}
          onChange={evt => onChange(Q.CONSIDER_OTHER_COURSE, evt.currentTarget.value)}
        />
      )}

      {considerOtherCourses === false && (
        <InputWithConfirm
          key={Q.WHY_NOT_CONSIDER_OTHER_COURSE}
          id={Q.WHY_NOT_CONSIDER_OTHER_COURSE}
          value={whyNotConsiderOtherCourse}
          label={'Why did you not consider other courses?'}
          inputHelperText={pageConfig.ConsiderOtherCourseReason?.helpText}
          onChange={evt => onChange(Q.WHY_NOT_CONSIDER_OTHER_COURSE, evt.currentTarget.value)}
        />
      )}

      <RadioOptionsWithConfirm
      id={Q.COURSE_ALIGN_FUTURE_CAREER}
        label="Does your course align with your future career plans?"
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: courseAlignFutureCareer === true
          },
          {
            char: 'N',
            label: 'No',
            selected: courseAlignFutureCareer === false
          }
        ]}
        maxQuestions={2}
        isInline={true}
        onClick={(question: IQuestion) => onChange(Q.COURSE_ALIGN_FUTURE_CAREER, question.label === 'Yes')}
      />

      {courseAlignFutureCareer === false && (
        <InputWithConfirm
          key={Q.COURSE_ALIGN_FUTURE_CAREER_OTHER}
          id={Q.COURSE_ALIGN_FUTURE_CAREER_OTHER}
          value={courseAlignFutureCareerOther}
          label={'Please explain why'}
          inputHelperText={pageConfig.CourseAlignCareerReason.helpText}
          onChange={(evt: any) => onChange(Q.COURSE_ALIGN_FUTURE_CAREER_OTHER, evt.currentTarget.value)}
        />
      )}
    </QuestionPage>
  );
};

export default InterviewQPage2;
